/**
 * Context Binding Engine — merge resolutions into per-field fills.
 *
 * The resolver emits one `CbeResolution` per matched binding row. Many rows
 * may target the SAME field (the same tag fanned out, or two facts of the
 * same tag). The merge step decides, PER FIELD:
 *
 *   - ORDER by precedenceRank (lower rank = higher authority, wins).
 *   - COMPLEMENT, not clobber: a `'complements'` binding only GAP-FILLS a
 *     field that has no higher-authority / manual value. It NEVER overwrites
 *     a populated owner value.
 *   - SURFACE conflicts: when two values for the same field DISAGREE (and
 *     neither can silently win), emit a `CbeFieldConflict` rather than
 *     silently picking one. Mirrors the PrecedenceResolution directive
 *     (never hard-block; always surface a resolution affordance).
 *
 * "Existing field state" (the value a field already holds from BOM / Intent
 * / a manual edit) is passed in by the caller via `FieldState`. 
 *
 * WIRING STATUS: all four callers are LIVE.
 *   1. Financial pull  — contextFinancialFill.ts / pullFinancialFromSources store action.
 *   2. SED-send        — buildSiteContextBlock (reportPrompts.ts), CBE-A2 markers.
 *   3. Bi-dir sync     — syncSedContextBindings.ts / sedAmendments store path.
 *   4. Intent fill     — contextIntentFill.ts / the store's claim-merge paths
 *                        (updateSiteContext + PL3 doc-upload) gap-fill
 *                        `surface:'intent'` rows into ProjectIntent.
 *
 * The merge is additionally exercised by its own tests with
 * synthetic field states.
 *
 * Every ratio / comparison is value-type-safe and NaN-guarded.
 *
 * Pure / synchronous. No I/O.
 */
import type {
  CbeOwnership,
  CbeResolution,
  CbeSurface,
  CbeValue,
} from './cbeTypes';

/** Authority of an existing (pre-context) value on a field. */
export type FieldAuthority =
  /** No value yet — a blank the context may fill. */
  | 'blank'
  /** A user-typed / locked value the context must NEVER overwrite. */
  | 'manual'
  /** Owned by a primary source (BOM / Intent) — context may only gap-fill
   *  a blank, never overwrite; a divergence surfaces as a conflict. */
  | 'owner'
  /** A derived / auto value the context MAY refresh. */
  | 'derived';

/** The existing value + authority of a target field, keyed by `surface:path`. */
export interface FieldState {
  readonly authority: FieldAuthority;
  /** The existing value, if any (for divergence detection). */
  readonly value?: unknown;
}

/** A field key — `surface:path`. */
function fieldKey(surface: CbeSurface, path: string): string {
  return `${surface}:${path}`;
}

/** A resolved fill the merge step decided to APPLY. */
export interface CbeFieldFill {
  readonly surface: CbeSurface;
  readonly path: string;
  readonly value: CbeValue;
  readonly ownership: CbeOwnership;
  readonly precedenceRank: number;
  readonly lowConfidence: boolean;
  readonly label: string;
}

/** A surfaced disagreement the merge step refused to silently resolve. */
export interface CbeFieldConflict {
  readonly surface: CbeSurface;
  readonly path: string;
  /** The context value that disagrees. */
  readonly contextValue: CbeValue;
  /** The pre-existing value it disagrees with (owner / manual / another
   *  context value). */
  readonly existingValue: unknown;
  readonly reason: CbeConflictReason;
  readonly label: string;
}

export type CbeConflictReason =
  /** Context disagrees with a populated OWNER value (BOM / Intent). */
  | 'owner-divergence'
  /** Context disagrees with a MANUAL value (never overwrite). */
  | 'manual-divergence'
  /** Two equal-rank context values for the same field disagree. */
  | 'context-context-divergence';

/** Result of merging — applied fills + surfaced conflicts + skipped rows. */
export interface CbeMergeResult {
  readonly fills: readonly CbeFieldFill[];
  readonly conflicts: readonly CbeFieldConflict[];
  /** Resolutions dropped because a higher-authority value already exists
   *  AND agrees (or the row was lower-priority than the chosen fill). */
  readonly suppressed: readonly CbeResolution[];
}

/**
 * Compare two values for equality at the granularity that matters for
 * conflict detection. NaN-safe. Ranges compare endpoint-wise; scalars use a
 * small relative tolerance so a float round-trip of the same figure matches.
 */
export function valuesAgree(a: CbeValue, b: unknown): boolean {
  // Existing value may be a primitive (from a real field) or a CbeValue
  // (from another resolution). Normalise the comparable scalar / range.
  const aScalar = comparableScalar(a);
  const bScalar = comparableScalarUnknown(b);
  if (aScalar !== null && bScalar !== null) {
    const denom = Math.max(Math.abs(aScalar), Math.abs(bScalar), 1);
    return Math.abs(aScalar - bScalar) / denom <= 1e-6;
  }
  const aRange = a.kind === 'range' ? a : null;
  const bRange = isCbeValue(b) && b.kind === 'range' ? b : null;
  if (aRange && bRange) {
    return nullableEq(aRange.min, bRange.min) && nullableEq(aRange.max, bRange.max);
  }
  // String-ish comparison for enum / date / complex / bool.
  const aStr = comparableString(a);
  const bStr = comparableStringUnknown(b);
  if (aStr !== null && bStr !== null) return aStr === bStr;
  return false;
}

function nullableEq(a: number | null, b: number | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  const denom = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) / denom <= 1e-6;
}

function isCbeValue(v: unknown): v is CbeValue {
  return typeof v === 'object' && v !== null && 'kind' in v && 'provenance' in v;
}

function comparableScalar(v: CbeValue): number | null {
  if (v.kind === 'scalar' && typeof v.value === 'number') return v.value;
  if (v.kind === 'multi') return v.total;
  return null;
}

function comparableScalarUnknown(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (isCbeValue(v)) return comparableScalar(v);
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) && v.trim() !== '' ? n : null;
  }
  return null;
}

function comparableString(v: CbeValue): string | null {
  if (v.kind === 'enum' || v.kind === 'complex') return v.value.trim().toLowerCase();
  if (v.kind === 'date') return v.iso;
  if (v.kind === 'bool') return String(v.value);
  if (v.kind === 'scalar' && typeof v.value === 'string') return v.value.trim().toLowerCase();
  return null;
}

function comparableStringUnknown(v: unknown): string | null {
  if (typeof v === 'string') return v.trim().toLowerCase();
  if (typeof v === 'boolean') return String(v);
  if (isCbeValue(v)) return comparableString(v);
  return null;
}

/**
 * Merge resolved fills against existing field state. Pure.
 *
 * @param resolutions the FILLED resolutions (caller should pass only those
 *                    with a `value`; unfilled rows are ignored here).
 * @param fieldStates existing value + authority per `surface:path`. Absent
 *                    key ⇒ treated as `{authority:'blank'}`.
 */
export function mergeResolutions(
  resolutions: readonly CbeResolution[],
  fieldStates: ReadonlyMap<string, FieldState> = new Map(),
): CbeMergeResult {
  const fills: CbeFieldFill[] = [];
  const conflicts: CbeFieldConflict[] = [];
  const suppressed: CbeResolution[] = [];

  // Bucket filled resolutions by target field.
  const byField = new Map<string, CbeResolution[]>();
  for (const r of resolutions) {
    if (r.value === undefined) {
      suppressed.push(r);
      continue;
    }
    const key = fieldKey(r.targetField.surface, r.targetField.path);
    const list = byField.get(key) ?? [];
    list.push(r);
    byField.set(key, list);
  }

  for (const [key, group] of byField) {
    const first = group[0]!;
    const { surface, path } = first.targetField;
    const state = fieldStates.get(key) ?? { authority: 'blank' as FieldAuthority };

    // ── Order context rows by precedence (lower rank wins). NaN-guard the
    // rank so a malformed binding never poisons the sort. ──────────────
    const ordered = [...group].sort((a, b) => {
      const ra = Number.isFinite(a.binding.fillPolicy.precedenceRank) ? a.binding.fillPolicy.precedenceRank : Number.POSITIVE_INFINITY;
      const rb = Number.isFinite(b.binding.fillPolicy.precedenceRank) ? b.binding.fillPolicy.precedenceRank : Number.POSITIVE_INFINITY;
      return ra - rb;
    });
    const winner = ordered[0]!;
    const winnerValue = winner.value!;

    // ── Surface intra-context divergence: any lower-priority context row
    // that DISAGREES with the winner is a conflict to surface, not a
    // silent drop. Agreeing rows are suppressed. ──────────────────────
    for (let i = 1; i < ordered.length; i += 1) {
      const other = ordered[i]!;
      if (valuesAgree(winnerValue, other.value!)) {
        suppressed.push(other);
      } else {
        conflicts.push({
          surface, path,
          contextValue: other.value!,
          existingValue: winnerValue,
          reason: 'context-context-divergence',
          label: other.binding.label,
        });
      }
    }

    // ── Decide the winner against the EXISTING field state. ────────────
    switch (state.authority) {
      case 'blank':
        // Gap-fill — both 'owns' and 'complements' may fill a blank.
        fills.push(toFill(winner, winnerValue));
        break;

      case 'manual':
        // NEVER overwrite a manual value. If it disagrees, surface it.
        if (state.value !== undefined && !valuesAgree(winnerValue, state.value)) {
          conflicts.push({
            surface, path,
            contextValue: winnerValue,
            existingValue: state.value,
            reason: 'manual-divergence',
            label: winner.binding.label,
          });
        } else {
          suppressed.push(winner);
        }
        break;

      case 'owner':
        // A primary owner (BOM / Intent) holds this field. Context only
        // gap-fills a blank (handled above); a populated owner is never
        // overwritten. Agree ⇒ suppress; disagree ⇒ surface conflict.
        if (state.value !== undefined && !valuesAgree(winnerValue, state.value)) {
          conflicts.push({
            surface, path,
            contextValue: winnerValue,
            existingValue: state.value,
            reason: 'owner-divergence',
            label: winner.binding.label,
          });
        } else {
          suppressed.push(winner);
        }
        break;

      case 'derived':
        // Derived / auto values are refreshable by context (if the binding
        // is refreshable). Disagreement is fine — context refreshes.
        if (winner.binding.fillPolicy.refreshable !== false) {
          fills.push(toFill(winner, winnerValue));
        } else {
          suppressed.push(winner);
        }
        break;

      default: {
        const _exhaustive: never = state.authority;
        suppressed.push(winner);
        void _exhaustive;
      }
    }
  }

  return { fills, conflicts, suppressed };
}

function toFill(r: CbeResolution, value: CbeValue): CbeFieldFill {
  return {
    surface: r.targetField.surface,
    path: r.targetField.path,
    value,
    ownership: r.binding.fillPolicy.ownership,
    precedenceRank: r.binding.fillPolicy.precedenceRank,
    lowConfidence: r.lowConfidence,
    label: r.binding.label,
  };
}
